import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Card,
  Tag,
  Typography,
  Divider,
  Input,
  Checkbox,
  List,
  Avatar,
  Spin,
  Pagination,
  Button,
  Space,
  Alert,
} from 'antd';
import {
  ContactsOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { contactApi, broadcastGroupApi, hashtagApi } from '../../../services/contactApi';
import { useLanguage } from '../../../contexts/LanguageContext';

const { Text } = Typography;

/**
 * Webhook 流程使用權限選擇 Modal
 *
 * 設計參考 RecipientSelector 的「聯絡人」分頁，但用途不同：
 * - 收件人控件決定「誰會收到訊息」
 * - 本控件決定「誰可以用電話啟動此 webhook 流程」
 *
 * 因此只保留 contacts / broadcastGroups / hashtags 三類；不含 users、流程變量、流程啟動人。
 *
 * value 結構：
 *   {
 *     contacts: [{ id, name }],   // 個別允許的聯絡人（用 id；後端會去 contact_lists 反查電話）
 *     groups:   [groupId],        // 廣播群組 id
 *     hashtags: [hashtagId],      // 聯絡人標籤 id
 *   }
 */
const WebhookAccessControlModal = ({
  visible,
  onCancel,
  onConfirm,
  value,
}) => {
  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactTotalCount, setContactTotalCount] = useState(0);
  const [contactSearchText, setContactSearchText] = useState('');
  const [contactCurrentPage, setContactCurrentPage] = useState(1);
  const contactPageSize = 20;

  const [broadcastGroups, setBroadcastGroups] = useState([]);
  const [hashtags, setHashtags] = useState([]);

  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedHashtags, setSelectedHashtags] = useState([]);

  const lastValueRef = useRef(null);

  // 每次打開時用 value 初始化內部狀態
  useEffect(() => {
    if (!visible) return;
    const v = value || {};
    setSelectedContacts(Array.isArray(v.contacts) ? v.contacts : []);
    setSelectedGroups(Array.isArray(v.groups) ? v.groups : []);
    setSelectedHashtags(Array.isArray(v.hashtags) ? v.hashtags : []);
    setContactCurrentPage(1);
    setContactSearchText('');
    lastValueRef.current = v;
  }, [visible]);

  // 載入廣播群組 + 標籤（只需一次）
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        const [groupsRes, hashtagsRes] = await Promise.all([
          broadcastGroupApi.getGroups(),
          hashtagApi.getHashtags(),
        ]);
        if (cancelled) return;
        const groups = Array.isArray(groupsRes) ? groupsRes : groupsRes?.data || [];
        const tags = Array.isArray(hashtagsRes) ? hashtagsRes : hashtagsRes?.data || [];
        setBroadcastGroups(groups);
        setHashtags(tags);
      } catch (err) {
        console.error('WebhookAccessControlModal 載入群組/標籤失敗:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // 載入聯絡人（隨分頁 + 搜尋變化）
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await contactApi.getContacts({
          page: contactCurrentPage,
          pageSize: contactPageSize,
          search: contactSearchText || undefined,
        });
        if (cancelled) return;
        setContacts(res?.contacts || []);
        setContactTotalCount(res?.totalCount || 0);
      } catch (err) {
        console.error('WebhookAccessControlModal 載入聯絡人失敗:', err);
        setContacts([]);
        setContactTotalCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, contactCurrentPage, contactSearchText]);

  const toggleContact = (contact, checked) => {
    if (checked) {
      setSelectedContacts((prev) => {
        if (prev.some((c) => String(c.id) === String(contact.id))) return prev;
        return [...prev, { id: contact.id, name: contact.name }];
      });
    } else {
      setSelectedContacts((prev) => prev.filter((c) => String(c.id) !== String(contact.id)));
    }
  };

  const toggleGroup = (groupId, checked) => {
    setSelectedGroups((prev) => {
      if (checked) return prev.includes(groupId) ? prev : [...prev, groupId];
      return prev.filter((id) => id !== groupId);
    });
  };

  const toggleHashtag = (hashtagId, checked) => {
    setSelectedHashtags((prev) => {
      if (checked) return prev.includes(hashtagId) ? prev : [...prev, hashtagId];
      return prev.filter((id) => id !== hashtagId);
    });
  };

  const handleClearAll = () => {
    setSelectedContacts([]);
    setSelectedGroups([]);
    setSelectedHashtags([]);
  };

  const handleConfirm = () => {
    const payload = {
      contacts: selectedContacts.map((c) => ({ id: c.id, name: c.name })),
      groups: [...selectedGroups],
      hashtags: [...selectedHashtags],
    };
    onConfirm?.(payload);
  };

  const totalSelected = selectedContacts.length + selectedGroups.length + selectedHashtags.length;

  return (
    <Modal
      title={t('workflowDesigner.webhookAccess.modalTitle')}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={640}
      zIndex={1050}
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t('workflowDesigner.webhookAccess.modalIntro')}
      />

      {/* 已選擇摘要 */}
      {totalSelected > 0 && (
        <Card size="small" style={{ marginBottom: 12 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Text strong>{t('workflowDesigner.webhookAccess.selectedSummary')}</Text>
            <Button type="text" size="small" icon={<ClearOutlined />} onClick={handleClearAll}>
              {t('recipientSelector.clearAll')}
            </Button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {selectedGroups.map((gid) => {
              const g = broadcastGroups.find((x) => x.id === gid);
              return (
                <Tag key={`g-${gid}`} color="orange" closable onClose={() => toggleGroup(gid, false)}>
                  {t('recipientSelector.tagLabels.group')}: {g?.name || gid}
                </Tag>
              );
            })}
            {selectedHashtags.map((hid) => {
              const h = hashtags.find((x) => x.id === hid);
              return (
                <Tag key={`h-${hid}`} color="purple" closable onClose={() => toggleHashtag(hid, false)}>
                  {t('recipientSelector.tagLabels.hashtag')}: {h?.name || hid}
                </Tag>
              );
            })}
            {selectedContacts.map((c) => (
              <Tag
                key={`c-${c.id}`}
                color="green"
                closable
                onClose={() => toggleContact(c, false)}
              >
                <ContactsOutlined /> {c.name || c.id}
              </Tag>
            ))}
          </div>
        </Card>
      )}

      {/* 廣播群組 */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>{t('recipientSelector.contactsTab.broadcastGroups')}:</Text>
        <div style={{ marginTop: 8 }}>
          {broadcastGroups.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {broadcastGroups.map((group) => (
                <Tag.CheckableTag
                  key={group.id}
                  checked={selectedGroups.includes(group.id)}
                  onChange={(checked) => toggleGroup(group.id, checked)}
                  style={{
                    backgroundColor: selectedGroups.includes(group.id) ? '#1890ff' : '#f0f0f0',
                    color: selectedGroups.includes(group.id) ? '#fff' : '#666',
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    padding: '4px 12px',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  {group.name}
                </Tag.CheckableTag>
              ))}
            </div>
          ) : (
            <div
              style={{
                padding: 20,
                textAlign: 'center',
                color: '#999',
                backgroundColor: '#f5f5f5',
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              {t('recipientSelector.contactsTab.noGroupsData')}
            </div>
          )}
        </div>
      </div>

      {/* 標籤 */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>{t('recipientSelector.contactsTab.hashtags')}:</Text>
        <div style={{ marginTop: 8 }}>
          {hashtags.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {hashtags.map((h) => (
                <Tag.CheckableTag
                  key={h.id}
                  checked={selectedHashtags.includes(h.id)}
                  onChange={(checked) => toggleHashtag(h.id, checked)}
                  style={{
                    backgroundColor: selectedHashtags.includes(h.id) ? '#52c41a' : '#f0f0f0',
                    color: selectedHashtags.includes(h.id) ? '#fff' : '#666',
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    padding: '4px 12px',
                    cursor: 'pointer',
                    fontSize: 12,
                  }}
                >
                  #{h.name}
                </Tag.CheckableTag>
              ))}
            </div>
          ) : (
            <div
              style={{
                padding: 20,
                textAlign: 'center',
                color: '#999',
                backgroundColor: '#f5f5f5',
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              {t('recipientSelector.contactsTab.noHashtagsData')}
            </div>
          )}
        </div>
      </div>

      <Divider />

      {/* 聯絡人列表 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <Text strong>{t('recipientSelector.contactsTab.contactList')}:</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('recipientSelector.contactsTab.totalContacts', { count: contactTotalCount })}
        </Text>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Input
          placeholder={t('recipientSelector.searchContacts')}
          prefix={<SearchOutlined />}
          value={contactSearchText}
          onChange={(e) => {
            setContactSearchText(e.target.value);
            setContactCurrentPage(1);
          }}
        />
      </div>

      <Spin spinning={loading}>
        <List
          dataSource={contacts}
          renderItem={(contact) => {
            const checked = selectedContacts.some(
              (c) => String(c.id) === String(contact.id)
            );
            return (
              <List.Item>
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Checkbox
                    checked={checked}
                    onChange={(e) => toggleContact(contact, e.target.checked)}
                    style={{ marginRight: 12 }}
                  />
                  <Avatar icon={<ContactsOutlined />} style={{ marginRight: 12 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold' }}>{contact.name}</div>
                    <div style={{ color: '#666', fontSize: 12 }}>
                      {contact.whatsAppNumber && `WhatsApp: ${contact.whatsAppNumber}`}
                      {contact.email && ` | Email: ${contact.email}`}
                    </div>
                  </div>
                </div>
              </List.Item>
            );
          }}
          locale={{ emptyText: t('recipientSelector.contactsTab.noContactsFound') }}
        />

        {contactTotalCount > contactPageSize && (
          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <Pagination
              current={contactCurrentPage}
              pageSize={contactPageSize}
              total={contactTotalCount}
              onChange={setContactCurrentPage}
              showSizeChanger={false}
              showQuickJumper
              showTotal={(total, range) =>
                t('recipientSelector.contactsTab.pageRange', {
                  start: range[0],
                  end: range[1],
                  total,
                })
              }
              size="small"
            />
          </div>
        )}
      </Spin>

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Space>
          <Button onClick={handleClearAll}>{t('recipientSelector.clear')}</Button>
          <Button type="primary" onClick={handleConfirm}>
            {t('recipientSelector.confirmSelection')}
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

export default React.memo(WebhookAccessControlModal);
